import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
    Sparkle,
    Briefcase,
    GraduationCap,
    IdentificationBadge,
    CheckCircle,
    Warning,
    Upload,
    X,
} from "@phosphor-icons/react";

const PHASE_META = {
    2: {
        icon: Briefcase,
        title: "Boost your visibility",
        pitch: "Recruiters spend 30% longer on profiles with employment & education history. Takes 60 seconds.",
        cta: "Add employment & education",
        route: "/candidate/profile",
    },
    3: {
        icon: IdentificationBadge,
        title: "Verify your identity",
        pitch: "Verified candidates get up to 3× more interview invites. Upload Aadhar + PAN — our AI verifies instantly.",
        cta: "Upload documents",
        route: "/candidate/profile",
    },
};

export default function ProfilePitchModal() {
    const [state, setState] = useState(null);
    const [open, setOpen] = useState(false);
    const [phase, setPhase] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/me/profile/completion")
            .then((r) => {
                setState(r.data);
                const c = r.data;
                if (!c.phase1_complete) return; // Skip pitches until basic profile is done
                // Choose the next phase to pitch — prefer phase 2, else phase 3.
                let next = null;
                if (!c.phase2_complete && !recentlyDismissed(c.phase2_dismissed_at)) next = 2;
                else if (!c.phase3_complete && !recentlyDismissed(c.phase3_dismissed_at)) next = 3;
                if (next) {
                    setPhase(next);
                    setOpen(true);
                }
            })
            .catch(() => {});
    }, []);

    const dismiss = async () => {
        if (!phase) { setOpen(false); return; }
        setBusy(true);
        try {
            await api.post("/me/profile/pitch/dismiss", { phase });
        } catch { /* silent */ }
        setBusy(false);
        setOpen(false);
    };

    if (!phase) return null;
    const meta = PHASE_META[phase];
    const Icon = meta.icon;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
            <DialogContent data-testid={`profile-pitch-modal-phase${phase}`} className="max-w-md p-0 gap-0">
                <DialogHeader className="px-5 pt-5 pb-3">
                    <div className="flex items-start gap-3">
                        <div className="w-11 h-11 rounded-full bg-cobalt/10 text-cobalt flex items-center justify-center shrink-0">
                            <Icon size={22} weight="bold" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-0.5">
                                Phase {phase} · optional
                            </p>
                            <DialogTitle className="text-lg font-black tracking-tight text-ink">
                                {meta.title}
                            </DialogTitle>
                        </div>
                    </div>
                </DialogHeader>
                <div className="px-5 pb-4">
                    <p className="text-sm text-[#525252] leading-relaxed">{meta.pitch}</p>
                    <div className="mt-4 grid grid-cols-3 gap-1 text-center">
                        <PhaseDot done={state?.phase1_complete} label="Basics" />
                        <PhaseDot done={state?.phase2_complete} active={phase === 2} label="Details" />
                        <PhaseDot done={state?.phase3_complete} active={phase === 3} label="Verify" />
                    </div>
                </div>
                <DialogFooter className="px-5 py-3 border-t border-[#E5E5E5] gap-2">
                    <button
                        type="button"
                        data-testid={`profile-pitch-later-${phase}`}
                        onClick={dismiss}
                        disabled={busy}
                        className="px-3 py-2 text-[10px] font-black uppercase tracking-wider border border-[#E5E5E5] text-[#525252] hover:border-ink"
                    >
                        Not now
                    </button>
                    <Link
                        to={meta.route}
                        data-testid={`profile-pitch-cta-${phase}`}
                        onClick={() => setOpen(false)}
                        className="px-3 py-2 text-[10px] font-black uppercase tracking-wider bg-ink text-white inline-flex items-center gap-1"
                    >
                        <Sparkle size={12} weight="bold" /> {meta.cta}
                    </Link>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PhaseDot({ done, active, label }) {
    return (
        <div className="flex flex-col items-center gap-1">
            <div className={`w-8 h-8 flex items-center justify-center rounded-full text-[11px] font-black ${
                done ? "bg-[#059669] text-white" :
                active ? "bg-cobalt text-white ring-4 ring-cobalt/20" :
                "bg-[#F5F5F5] text-[#A3A3A3]"
            }`}>
                {done ? <CheckCircle size={16} weight="fill" /> : (active ? "…" : "○")}
            </div>
            <span className={`text-[9px] font-black uppercase tracking-wider ${
                done ? "text-[#059669]" : active ? "text-cobalt" : "text-[#A3A3A3]"
            }`}>{label}</span>
        </div>
    );
}

// Dismiss for 7 days so a candidate isn't nagged every login.
function recentlyDismissed(iso) {
    if (!iso) return false;
    const then = new Date(iso).getTime();
    if (isNaN(then)) return false;
    return (Date.now() - then) < 7 * 24 * 60 * 60 * 1000;
}
